import { EventEmitter } from '@angular/core';

import { IReactiveValidator, IReactiveContext } from "../reactive-context";

export class IdDescriptionPair {

    // data members

    id: any;
    description: string;

    // constructor

    constructor(id: any, description: string = "") {

        this.id = id;
        this.description = description;
    }
}

export class PropertyChangeEvent {

    // data members

    private _name: string;
    get name() {
        return this._name;
    }

    private _value: any;
    get value() {
        return this._value;
    }
    private _modified = false;
    get modified() {
        return this._modified;
    }
    private _valid = true;
    get valid() {
        return this._valid;
    }

    // constructor

    constructor(name: string, value: any, modified: boolean, valid: boolean) {

        this._name = name;
        this._value = value;
        this._modified = modified;
        this._valid = valid;
    }
}

export class SeReactiveProperty<T> {

    // events

    propertyChange: EventEmitter<PropertyChangeEvent> = new EventEmitter<PropertyChangeEvent>();

    // data members

    private _name: string;
    get name() {
        return this._name;
    }

    private _modified: boolean = false;
    get modified() {
        return this._modified;
    }

    private _valid: boolean = true;
    get valid() {
        return this._valid;
    }

    private _value: T;
    get value(): T {
        return this._value;
    }
    set value(value: T) {
        this.setValue(value, true);
    }

    get errors() {

        let errors = new Array<string>();
        if (this._validators == null)
            return;

        this._validators.forEach(validator => errors.push(validator.error));

        return errors;
    }

    setValue(value: T, emitModifyEvent: boolean) {
        this._value = value;
        this._valid = this._validators == null ? true : this._validators.every(validator => validator.validate(value));
        if (emitModifyEvent) {
            if (this._selectorPropertyName && this._initialValue && this.value) {
                this._modified = !this._comparator(this._initialValue[this._selectorPropertyName], this.value[this._selectorPropertyName]);
            } else {
                this._modified = !this._comparator(this._initialValue, this.value);
            }
            this.propertyChange.emit(new PropertyChangeEvent(this.name, this._value, this._modified, this._valid));
        }
    }

    private _initialValue: T;
    private _validators: Array<IReactiveValidator>;
    private _selectorPropertyName: string;
    private _comparator: (a: T, b: T) => boolean;

    // constructor

    constructor(
        name: string,
        initialValue: T,
        comparator: (a: T, b: T) => boolean,
        selectorPropertyName?: string,
        validators?: Array<IReactiveValidator>
    ) {
        this._name = name;
        this._initialValue = initialValue;
        this._selectorPropertyName = selectorPropertyName;
        this._validators = validators;
        this._comparator = comparator;

        this.setValue(initialValue, false);
    }

    // public functions

    reset() {
        this._initialValue = this._value;
        this.value = this._initialValue;
    }
}

export class SeReactiveComplexProperty extends SeReactiveProperty<SeReactiveViewModel<any>> {

    // data members

    get modified() {
        return this.value.modified;
    }

    get valid() {
        return this.value.valid;
    }

    get errors() {
        return this.value.errors;
    }

    // constructors

    constructor(
        name: string,
        reactiveViewModel: SeReactiveViewModel<any>,
        comparator: (a: any, b: any) => boolean,
        selectorPropertyName?: string,
        validators?: Array<IReactiveValidator>
    ) {
        super(name, reactiveViewModel, comparator, selectorPropertyName, validators);

        reactiveViewModel.propertyChange.subscribe((property) => {
            this.propertyChange.emit(property);
        });
    }

    // public functions

    reset() {
        this.value.bindToView();
    }
}

export class SeReactiveViewModel<TModel, TExtra = any> implements IReactiveContext<TModel> {

    // events

    propertyChange: EventEmitter<PropertyChangeEvent> = new EventEmitter<PropertyChangeEvent>();

    // data members

    private _model: TModel;
    get model(): TModel {
        return this._model;
    }

    private _extraData: TExtra;
    get extraData(): TExtra {
        return this._extraData;
    }

    get modified() {
        return Array.from(this._properties.values()).some(property => property.modified);
    }

    get modifiedSimple() {
        return this.simpleProperties.some(property => property.modified);
    }

    get valid() {
        return Array.from(this._properties.values()).every(property => property.valid);
    }

    get validSimple() {
        return this.simpleProperties.every(property => property.valid);
    }

    get errors() {
        let errors = new Array<string>();
        Array.from(this._properties.values()).forEach(properties => properties.errors.forEach(error => errors.push(error)));

        return errors;
    }

    get errorsSimple() {
        let errors = new Array<string>();
        this.simpleProperties.forEach(properties => properties.errors.forEach(error => errors.push(error)));

        return errors;
    }

    private get simpleProperties() {
        return Array.from(this._properties.values()).filter(property => !(property instanceof SeReactiveComplexProperty));
    }

    private get complexProperties() {
        return Array.from(this._properties.values()).filter(property => property instanceof SeReactiveComplexProperty);
    }

    private _properties: Map<string, SeReactiveProperty<any>> = new Map<string, SeReactiveProperty<any>>();
    private _propertySubscribers: Map<string, any> = new Map<string, any>();

    // constructor

    constructor(model: TModel, extraData?: TExtra) {
        this._model = model;
        this._extraData = extraData;

        this.registerProperties(model);
    }

    // public functions

    registerProperty(name: string, initialValue: any, selectorPropertyName?: string, validators?: Array<IReactiveValidator>, comparator = (a, b) => a == b) {

        let property = new SeReactiveProperty(name, initialValue, comparator, selectorPropertyName, validators);
        this.internalRegisterProperty(name, property);
    }

    registerComplexProperty(name: string, reactiveViewModel: SeReactiveViewModel<any>, comparator = (a, b) => a == b) {

        let property = new SeReactiveComplexProperty(name, reactiveViewModel, comparator);
        this.internalRegisterProperty(name, property);
    }

    unregisterProperty(name: string) {

        this._properties.delete(name);
        this._propertySubscribers[name].unsubscribe();
        this._propertySubscribers.delete(name);
    }

    unregisterProperties() {

        Array.from(this._propertySubscribers.values()).forEach(subscriber => subscriber.unsubscribe());
        this._propertySubscribers.clear();
        this._properties.clear();
    }

    property(name: string) {
        return this._properties.get(name);
    }

    public bindToView(model?: TModel) {
        if (model) {
            this._model = model;
            this.registerProperties(model);
        }
        else
            this._properties.forEach(property => property.reset());
    }

    public bindToModel() {
        // This is intentional
    }

    // protected functions

    protected onRegisterProperties(_model: TModel) {
        // This is intentional
    }

    // private functions

    private registerProperties(model: TModel) {
        this.onRegisterProperties(model);
    }

    private internalRegisterProperty(name: string, property: SeReactiveProperty<any>) {

        let subscriber = property.propertyChange.subscribe((property) => {
            this.propertyChange.emit(property);
        });

        this._propertySubscribers.set(name, subscriber);
        this._properties.set(name, property);
    }
}
